import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../../Helpers/Handler";
import { RULES } from "../../../Helpers/bookingRules";
import { currencyFormatter, formatDateTime } from "../../../Helpers/Utils";
import useListParams from "../../../Helpers/useListParams";
import TestModeTag from "../../../Components/TestModeTag";

// Top-level Bookings page - the sidebar's "Bookings" entry lands here.
// Read-only list + a small stage-funnel strip (reuses the same aggregation
// the Dashboard overview already computes) + the "Book Now" entry point into
// the new Availability -> Registration -> Payment Link flow.

const STAGES = ["Initiated", "Booked", "Started", "Ended", "Completed", "Pending", "Closed", "Cancelled"];

// Completed and Pending used to share bg-warning, making them
// indistinguishable at a glance in both this strip/table and the Booking
// Detail page's own badge - kept in sync with that page's STAGE_BADGE.
const STAGE_BADGE = {
    Initiated: "bg-secondary",
    Booked: "bg-primary",
    Started: "bg-info text-dark",
    Ended: "bg-info-subtle text-dark",
    Completed: "bg-dark",
    Pending: "bg-warning text-dark",
    Closed: "bg-success",
    Cancelled: "bg-danger",
};

const LIMIT = 12;
// paymentMode: "" (all), "live" or "test" (test mode).
const LIST_FILTERS = { search: "", bookingStage: "", dateFrom: "", dateTo: "", paymentMode: "" };

const BookingList = () => {
    const navigate = useNavigate();
    const [funnel, setFunnel] = useState(null);
    const [list, setList] = useState([]);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);

    // Page number in the URL path (/booking/page/3), filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilter, resetFilters } = useListParams(LIST_FILTERS, { zeroBased: true });
    const { search, bookingStage, dateFrom, dateTo, paymentMode } = filters;
    const setPaymentMode = (value) => setFilter("paymentMode", value);
    const setSearch = (value) => setFilter("search", value);
    const setBookingStage = (value) => setFilter("bookingStage", value);
    const setDateFrom = (value) => setFilter("dateFrom", value);
    const setDateTo = (value) => setFilter("dateTo", value);
    const searchDebounce = useRef(null);
    const lastSearch = useRef(search);

    useEffect(() => {
        (async () => {
            // Stage counts: live bookings, or only test ones when the Test filter is on (test mode).
            const response = await Handler({ method: "get", url: `/admin/dashboard/overview${paymentMode === "test" ? "?mode=test" : ""}` });
            if (response?.success) setFunnel(response?.data?.data?.bookings?.byStage || response?.data?.data?.bookingFunnel || null);
        })();
    }, [paymentMode]);

    const load = useCallback(async (pageNo) => {
        // A "to" before "from" matches nothing; the field shows why instead.
        if (dateFrom && dateTo && dateTo < dateFrom) return;
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: `/admin/booking/list/${pageNo}`,
            params: { limit: LIMIT },
            data: {
                sortBy: "createdAt",
                sort: true,
                ...(bookingStage ? { bookingStage } : {}),
                ...(paymentMode ? { paymentMode } : {}),
                // Matches booking ID, customer name/phone, or car registration
                // number - previously this only searched bookingId, so an
                // admin fielding a phone call had no way to look a booking up
                // by anything a customer would actually give them.
                ...(search.trim() ? { search: search.trim().slice(0, RULES.SEARCH_MAX) } : {}),
                ...(dateFrom ? { createdFrom: new Date(`${dateFrom}T00:00:00`).toISOString() } : {}),
                // Include the whole "to" day, not just its first instant.
                ...(dateTo ? { createdTo: new Date(`${dateTo}T23:59:59.999`).toISOString() } : {}),
            },
        });
        if (response?.success) {
            const data = response?.data?.data || {};
            setList(data.list || []);
            setHasNext(!!data.next);
        } else {
            setList([]);
            setHasNext(false);
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingStage, search, dateFrom, dateTo, paymentMode]);

    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        // Wait for typing to pause before searching; page / filter changes load straight away.
        const delay = search !== lastSearch.current ? 350 : 0;
        lastSearch.current = search;
        searchDebounce.current = setTimeout(() => load(page), delay);
        return () => clearTimeout(searchDebounce.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, page]);

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-semibold mb-0">Bookings</h5>
                <button className="btn btn-primary" onClick={() => navigate("/booking/availability")}>+ Book Now</button>
            </div>

            {/* Stage funnel strip */}
            {funnel && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                    {STAGES.map((stage) => (
                        <button
                            type="button"
                            key={stage}
                            className={`btn btn-sm ${bookingStage === stage ? "btn-dark" : "btn-outline-secondary"}`}
                            onClick={() => setBookingStage(bookingStage === stage ? "" : stage)}
                        >
                            {stage} <span className="badge bg-light text-dark ms-1">{funnel[stage] ?? 0}</span>
                        </button>
                    ))}
                </div>
            )}

            {funnel && paymentMode !== "test" ? <div className="small text-muted mb-3 mt-n2">Stage counts leave out test bookings. Choose "Test only" to count them.</div> : null}

            {/* Filters */}
            <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input type="text" className="form-control" placeholder="Booking ID, customer name/phone, or car reg." maxLength={RULES.SEARCH_MAX} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Stage</label>
                        <select className="form-select" value={bookingStage} onChange={(e) => setBookingStage(e.target.value)}>
                            <option value="">All stages</option>
                            {STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Created from</label>
                        <input type="date" className="form-control" max={dateTo || undefined} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Created to</label>
                        <input type="date" className={`form-control${dateFrom && dateTo && dateTo < dateFrom ? " is-invalid" : ""}`} min={dateFrom || undefined} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        {dateFrom && dateTo && dateTo < dateFrom && <div className="invalid-feedback">Must be on or after the from date</div>}
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Payments</label>
                        <select className="form-select" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} aria-label="Live or test bookings">
                            <option value="">Live and test</option>
                            <option value="live">Live only</option>
                            <option value="test">Test only</option>
                        </select>
                    </div>
                    <div className="col-md-1">
                        <button className="btn btn-outline-secondary w-100" onClick={resetFilters}>Reset</button>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="card border-0 shadow-sm rounded-4">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Booking ID</th>
                                <th>Customer</th>
                                <th>Car</th>
                                <th>Pickup</th>
                                <th>Drop-off</th>
                                <th>Stage</th>
                                <th className="text-end">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className="text-center text-muted py-4">Loading...</td></tr>
                            )}
                            {!loading && list.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-muted py-4">No bookings match.</td></tr>
                            )}
                            {!loading && list.map((b) => (
                                <tr key={b.bookingId} role="button" style={{ cursor: "pointer" }} onClick={() => navigate(`/booking/details/${b.bookingId}`)}>
                                    <td className="fw-semibold">{b.bookingId}<TestModeTag mode={b.paymentMode} /></td>
                                    <td>
                                        <div>{b.customerName || b.customerId}</div>
                                        {b.customerPhone && <div className="text-muted small">{b.customerPhone}</div>}
                                    </td>
                                    <td>{b.carRegistrationNo || b.carId}</td>
                                    <td>{b.preBookDetails?.startDate ? formatDateTime(b.preBookDetails.startDate) : "-"}</td>
                                    <td>{b.preBookDetails?.endDate ? formatDateTime(b.preBookDetails.endDate) : "-"}</td>
                                    <td><span className={`badge ${STAGE_BADGE[b.bookingStage] || "bg-secondary"}`}>{b.bookingStage}</span></td>
                                    <td className="text-end">{b.totalAmount ? currencyFormatter(b.totalAmount) : "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="d-flex justify-content-between p-3">
                    <button className="btn btn-outline-secondary btn-sm" disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</button>
                    <span className="text-muted small">Page {page + 1}</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={!hasNext || loading} onClick={() => setPage(page + 1)}>Next</button>
                </div>
            </div>
        </div>
    );
};

export default BookingList;
